"use strict";

import Constants from "/js/core/constants.js";
import Util from "/js/util/util.js";
import AppUtil from "/js/util/app-util";

/**
 * Business logic for generating a result of a lesson interaction.
 * "Static" class.
 */
export default class InteractionResultUtil {
  
  /**
   * Test the user's interaction answers against the result object's "output" elements and returns 
   * correct one.
   */
  static findCorrectOutput(answers, result, questions=null) {
    // cl('findCorrectOutput()', 'answers:', answers);
    // cl('findCorrectOutput()', 'result:', result);
    // cl('findCorrectOutput()', 'questions', questions);

    // Iterate the possible outputs in order and see if any of them pass the appropriate test.
    for (let i = 0; i < result.outputs.length; i++) {

      const output = result.outputs[i];
      const requirementType = output.requirement.type;

      let b;
      switch (requirementType) {
        case "keymatches":
          const answersArray = answers.answers || answers;
          b = this.doKeymatchesTest(answersArray, output, questions);
          break;
        case "tallymin":
          b = this.doTallyTest(true, answers, output, questions);
          break;
        case "tallymost":
          b = this.doTallyTest(false, answers, output, questions);
          break;
        case "default":
          b = this.doDefaultTest();
          break;
        default:
          clw("WARNING: Unrecognized requirement type:", requirementType);
          b = false;
          break;
      }  
      if (b) {
        return output;
      }
    }

    clw("WARNING: No output object was selected.");
    return null;
  }

  /**
   * This is tricky.
   *
   * Examples:
   * http://localhost:8080/primer.html?lessonid=brandmessaging&ix=first
   */
  static doKeymatchesTest(answers, output, questions) {
    // cl('doKeymatchesTest() - answers:', answers);
    // cl('doKeymatchesTest() - output:', output);
    // cl('doKeymatchesTest() - questions:', questions);

    if (output.requirement.type !== 'keymatches') {
      return false
    }

    const findResultKeyQuestionsLookup = () => {
      // Find the question that has a choices array whose elements have resultkey properties (yes rly)
      let questionIndex;
      let question;
      for (let i = 0; i < questions.length; i++) {
        const item = questions[i];
        if (item.choices && item.choices.length > 0 && item.choices[0].resultkey) {
          questionIndex = i;
          question = item;
          break;
        }
      }
      if (!question) {
        clw("WARNING: Question array has no keymatch specs");
        return null;
      }
      // cl('findResultKeyQuestionsLookup() - found question:', questionIndex, question);

      // Now find the "resultkey" selected by the user using the corresponding answer element.
      const answerIndex = answers[questionIndex][0];  // Rem, the element is an array (always 1 element)
      const resultKey = question.choices[answerIndex].resultkey;
      // cl('doKeymatchesTest.findResultKeyQuestionsLookup() - resultKey:', answerIndex, resultKey);
      return resultKey;
    };

    let resultKey;
    if (answers && answers[0] && answers[0].resultKey) {
      // This-or-that interaction puts the resultKey right in the answers.
      resultKey = answers[0].resultKey;
      // cl('doKeymatchesTest() - resultKey:', resultKey);
    } else {
      // Must do complicated lookup
      resultKey = findResultKeyQuestionsLookup();
    }

    // See if resultKey matches what's in outputObject.
    let hasMatch = false;
    for (let key of output.requirement.keymatches) {
      if (key === resultKey) {
        hasMatch = true;
        break;
      }
    }
    return hasMatch;
  }

  /**
   * Test where user answers must meet "tallymin" or 'tallymost" test" for all bucket key-values
   * listed (Typically there is only ever one key-value pair requirement).
   *
   * @param isMinNotMost
   * @param answers
   *    Note, the answers object can have one of two formats. One has a "left" and "right" property,
   *    and the other is an array of array indices.
   * @param output
   * @param questions
   * @return {boolean}
   */
  static doTallyTest(isMinNotMost, answers, output, questions) {
    // cl('doTallyTest()', 'isMinNotMost:', isMinNotMost);
    // cl('doTallyTest()', 'answers:', answers);
    // cl('doTallyTest()', 'output:', output);
    // cl('doTallyTest()', 'questions:', questions);

    if (!output.requirement) {
      clw("WARNING: 'output' missing required data:", output);
      return false;
    }

    const answerCounts = {};

    if (answers.left !== undefined) {

      // Special case: Put "left" and "right" property values in to buckets of the same name.
      answerCounts["left"] = answers.left.length;
      answerCounts["right"] = answers.right.length;

    } else if (answers.answers !== undefined) {

      // Iterate array of indices (array of arrays actually)
      for (let i = 0; i < answers.answers.length; i++) {
        const array = answers.answers[i];
        for (let el of array) {
          // Extra lookup. Complicated.
          const choice = questions[i].choices[el];
          const key = choice.countKey;
          if (!key) {
            clw("WARNING: countKey not found for question", i);
          } else {
            // Increment answerCounts[key] by 1
            answerCounts[key]
                = (answerCounts[key] === undefined) ? 1 : answerCounts[key] + 1;
          }
        }
      }
    }
    // cl('answerCounts: ', answerCounts);

    if (isMinNotMost) {
      // Do "tallymin" test
      const requiredCounts = output.requirement.buckets;
      for (let key in requiredCounts) {
        if (!requiredCounts.hasOwnProperty(key)) {
          continue;
        }
        const requiredValue = requiredCounts[key] || 0;
        const userValue = answerCounts[key] || 0;
        // cl('tallymin...  key:', key, 'requiredValue', requiredValue, 'userValue', userValue);
        if (userValue < requiredValue) {
          return false;
        }
      }
      return true;
    } else {
      // Do "tallymost" test
      let maxKey;
      let maxValue = -1;
      for (let key in answerCounts) {
        if (answerCounts.hasOwnProperty(key)) {
          const value = answerCounts[key] || 0;
          if (value > maxValue) {
            maxValue = value;
            maxKey = key;
            // Note how a tie does not displace current maxKey.
            // This is consistent with the mobile app.
          }
        }
      }
      const target = output.requirement.tallyTarget;
      return (maxKey === target);
    }
  }

  static doDefaultTest() {
    // cl('doDefaultTest()');
    return true; // Always passes!
  }

  /**
   * Replaces the user text input into the raw html text, plus any specific quirks that are needed.
   */
  static stringReplaceHtml(interaction, answers, rawHtmlText) {

    let html = this.#replaceHtmlLegacyColors(rawHtmlText) ;

    // The html can reference files that are expected to be in the lesson assets dir.
    // These paths in the html are supposed to look like this: "[0-APPDIR]/lesson-xxx/etc"
    // Ie, the lesson id subdirectory is already included in that path.
    html = rawHtmlText.replace("[0-APPDIR]/", Constants.lessonAssetsBasePath);

    const answersArray = answers.answers;

    html = this.#doStringReplaceAction(html, interaction.questions, answersArray);

    const resultObject = interaction.result;
    if (resultObject.logic) {
      const evalResult = this.#doEval(resultObject.logic, interaction.questions, answersArray);
      html = Util.stringReplaceKeysWithValues(html, evalResult);
    }

    return html;
  }

  /**
   * Loads external html file, does stringReplaceHtml() on it, and calls back.
   */
  static stringReplaceHtmlAsync(interaction, output, answers, callback) {

    const onError = (xhr, status, error) => {
      clw("ERROR: " + error);
      callback(null);
    };
    const onSuccess = (data, status, xhr) => {
      // Do string replace action and return result.
      const html = InteractionResultUtil.stringReplaceHtml(interaction, answers, data);
      callback(html);
    };

    // Make path and load html file.
    const crumb = output.resource;  // Rem, this path already starts with lesson id subdir!
    const path = AppUtil.makeLessonAssetPath(crumb);
    $.ajax({ url: path, dataType: "text", success: onSuccess, error: onError });
  }

  static #doStringReplaceAction(string, questions, answersArray) {
    // cl('doStringReplaceAction() - questions:', questions);
    // cl('doStringReplaceAction() - answers:', answersArray);

    // 1. Do a string replace using the hashobject at
    // [question > choices[user's-answer-index] > contents]
    questions.forEach((question, questionIndex) => {
      const choicesArray = question.choices;
      if (!choicesArray) {
        return;
      }

      // Get the user's answer index
      const answer = answersArray[questionIndex];  // an array w/ an expected length of 1
      if (!answer) {
        return;
      }
      const answerIndex = answer[0];
      // cl('answerIndex', answerIndex);

      // Get the corresponding hash object with the replacement data.
      const choice = choicesArray[answerIndex];
      if (!choice) {
        return;
      }
      const contentsHash = choice.contents;
      if (!contentsHash) {
        return;
      }
      cl('contentsHash:', contentsHash);
      string = Util.stringReplaceKeysWithValues(string, contentsHash);
      // Rem, the replaced contents themselves can have tokens which get replaced in the next step.
    });

    // 2. Replace "contentkeys" with user answers
    questions.forEach((question, questionIndex) => {
      const questionKeys = question.contentkeys;
      const answerValues = answersArray[questionIndex];
      if (! questionKeys || !answerValues) {
        return;
      }
      if (questionKeys.length !== answerValues.length) {
        clw("WARNING: Parallel arrays of inconsistent lengths:", questionKeys, answerValues);
      }
      questionKeys.forEach((key, keysIndex) => {
        let value = answerValues[keysIndex];
        value = Util.escapeStringForHtmlOutput(value);
        string = Util.stringReplaceAll(string, key, value);
      });
    });

    return string;
  }

  /**
   * Performs `eval()` using the result object's "logic" string etc.
   */
  static #doEval(evalString, questions, answersArray) {
    // The string is expected to look something like this:
    // "var val = [1-CONTENT-COST] / [1-AVG-IMPRESSIONS] ; return JSON.stringify({ '[1-EQUATION-RESULT]': val.toFixed(2) });"

    evalString = this.#doStringReplaceAction(evalString, questions, answersArray);

    // `eval()` does not take an explicit "return" statement, so strip it out. 
    // (The last statement of an `eval` is what gets returned, automatically).
    evalString = evalString.replace("return ", " ");

    let result = eval(evalString);
    // The result is expected to be a json string, so parse it.
    result = JSON.parse(result);
    return result;
  }

  // TODO: Implement this: https://cs.corp.google.com/piper///depot/google3/java/com/google/android/apps/primer/util/app/AppUtil.java?l=653
  static #replaceHtmlLegacyColors(html) {
    return html;
  }
}
